import { motion } from "motion/react";
import { Link } from "react-router-dom";
import {
  TrendingUp,
  Shield,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  Eye,
  Clock,
  Users,
  Award,
  Target
} from "lucide-react";

export function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-green-50/30 to-blue-50/30"></div>
          <motion.div
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-to-br from-green-400/10 to-blue-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 90, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-green-500/10 rounded-full blur-3xl"
            animate={{
              scale: [1.2, 1, 1.2],
              rotate: [90, 0, 90],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />

          {/* Grid Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, black 1px, transparent 0)',
              backgroundSize: '48px 48px'
            }}></div>
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-sm mb-8 border border-gray-200/50"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Award className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-700 font-medium">Confiança de empresas que lideram seus mercados</span>
            </motion.div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 leading-[1.1] tracking-tight">
              Seu negócio,<br />
              <span className="bg-gradient-to-r from-green-600 via-green-500 to-blue-600 bg-clip-text text-transparent">
                na palma da sua mão
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed">
              Decisões mais rápidas. Operação mais eficiente. Crescimento sustentável.<br />
              Gestão profissional para empresas que não param de crescer.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Link to="/demonstracao">
                <motion.button
                  className="px-8 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 flex items-center gap-2 group font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Ver como funciona
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>

              <Link to="/solucoes">
                <motion.button
                  className="px-8 py-4 bg-white/80 backdrop-blur-sm text-gray-900 rounded-xl hover:bg-white hover:shadow-xl transition-all duration-300 border border-gray-200/50 font-medium"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Conhecer soluções
                </motion.button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Implantação guiada</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Suporte dedicado</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
                <span>Dados sempre seguros</span>
              </div>
            </div>
          </motion.div>

          {/* Dashboard Mockup */}
          <motion.div
            className="mt-20 relative"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            <div className="relative mx-auto max-w-6xl">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl shadow-gray-900/10 p-4 md:p-8 border border-gray-200/50">
                <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-4 md:p-8 shadow-inner">
                  {/* Dashboard Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <div className="text-white/60 text-xs md:text-sm font-mono">Visão Executiva</div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {[
                      { label: "Faturamento", value: "R$ 2.4M", unit: "", change: "+18%", cardClassName: "bg-gradient-to-br from-green-500 to-green-600 p-4 md:p-6 rounded-xl", textClassName: "text-green-200 text-xs flex items-center gap-1" },
                      { label: "Margem", value: "32", unit: "%", change: "+5%", cardClassName: "bg-gradient-to-br from-blue-500 to-blue-600 p-4 md:p-6 rounded-xl", textClassName: "text-blue-200 text-xs flex items-center gap-1" },
                      { label: "Clientes Ativos", value: "847", unit: "", change: "+12%", cardClassName: "bg-gradient-to-br from-purple-500 to-purple-600 p-4 md:p-6 rounded-xl", textClassName: "text-purple-200 text-xs flex items-center gap-1" }
                    ].map((stat, i) => (
                      <motion.div
                        key={i}
                        className={stat.cardClassName}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.8 + i * 0.1 }}
                        whileHover={{ scale: 1.02 }}
                      >
                        <div className="text-white/80 text-xs md:text-sm mb-2">{stat.label}</div>
                        <div className="text-white text-2xl md:text-3xl font-bold mb-1">
                          {stat.value}<span className="text-lg">{stat.unit}</span>
                        </div>
                        <div className={stat.textClassName}>
                          <TrendingUp className="w-3 h-3" />
                          {stat.change} este mês
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Chart */}
                  <div className="bg-gray-800/50 rounded-xl p-4 md:p-6 h-32 md:h-48 flex items-end gap-1 md:gap-2">
                    {[65, 72, 68, 85, 92, 78, 88, 95, 82, 90, 94, 88].map((height, i) => (
                      <motion.div
                        key={i}
                        className="flex-1 bg-gradient-to-t from-green-500 to-green-400 rounded-t opacity-80 hover:opacity-100 transition-opacity"
                        initial={{ height: 0 }}
                        animate={{ height: `${height}%` }}
                        transition={{ delay: 1 + i * 0.05, duration: 0.5 }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-green-500/20 rounded-full blur-2xl"></div>
              <div className="absolute -top-4 -right-4 w-32 h-32 bg-blue-500/20 rounded-full blur-2xl"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sobre Section */}
      <section className="py-24 md:py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 leading-tight">
              Gestão empresarial não precisa ser complicada.<br />
              <span className="text-green-600">Precisa funcionar.</span>
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              A BusiFarm desenvolve plataformas que colocam informação estratégica na mão de quem decide.
              Sem complexidade. Sem curva de aprendizado infinita. Apenas resultados que você pode ver e usar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefícios Section */}
      <section className="py-24 md:py-32 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Por que empresas líderes escolhem a BusiFarm
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Porque gestão profissional não é só sobre tecnologia. É sobre crescer sem perder o controle.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Eye,
                title: "Visão completa do negócio",
                desc: "Todos os números que importam, em tempo real. Saiba exatamente onde está seu dinheiro, quem deve, quem comprou e o que precisa da sua atenção.",
                benefit: "Decisões mais rápidas e assertivas"
              },
              {
                icon: Target,
                title: "Controle total da operação",
                desc: "Do estoque ao financeiro, da produção às vendas. Tudo integrado, nada escapa. Você não gerencia sistemas, você gerencia o negócio.",
                benefit: "Zero retrabalho, zero surpresas"
              },
              {
                icon: TrendingUp,
                title: "Crescimento sustentável",
                desc: "Venda mais sem perder qualidade. Produza mais sem perder controle. Cresça sabendo que sua gestão cresce junto.",
                benefit: "Escale sem caos operacional"
              },
              {
                icon: Clock,
                title: "Economia de tempo brutal",
                desc: "Chega de refazer planilhas, caçar informação e conferir manualmente. A plataforma trabalha para você, não o contrário.",
                benefit: "Foque no que gera resultado"
              },
              {
                icon: Shield,
                title: "Dados protegidos e disponíveis",
                desc: "Seus dados são seus. Sempre acessíveis, sempre seguros, sempre atualizados. Trabalhe de qualquer lugar com total confiança.",
                benefit: "Continuidade garantida"
              },
              {
                icon: Users,
                title: "Suporte que resolve",
                desc: "Time dedicado que entende seu negócio e está ali quando você precisa. Não é chatbot, é gente que resolve problema de verdade.",
                benefit: "Você nunca fica na mão"
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
              >
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center mb-6 shadow-lg">
                  <item.icon className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed mb-4">{item.desc}</p>

                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-green-700 font-semibold">
                    <Sparkles className="w-4 h-4" />
                    <span className="text-sm">{item.benefit}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prova Social Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Resultado que você pode <span className="text-green-600">confiar</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { value: "12M+", label: "Operações gerenciadas" },
              { value: "99.9%", label: "Disponibilidade" },
              { value: "24/7", label: "Suporte disponível" },
            ].map((stat, i) => (
              <motion.div
                key={i}
                className="text-center"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Garantia Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-xl">
              <Shield className="w-10 h-10 text-white" />
            </div>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Sua operação não para.<br />
              Nem a nossa.
            </h2>

            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Implantação guiada por especialistas. Treinamento completo da sua equipe.
              Suporte dedicado sempre que precisar. E atualizações constantes sem você precisar fazer nada.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              {[
                { icon: CheckCircle2, text: "Migração segura dos seus dados" },
                { icon: CheckCircle2, text: "Treinamento completo incluído" },
                { icon: CheckCircle2, text: "Acompanhamento pós-implantação" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 text-left">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-24 px-6 bg-gradient-to-br from-green-600 via-green-700 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div
            className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"
            animate={{
              scale: [1, 1.3, 1],
              x: [0, 50, 0],
              y: [0, -50, 0],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div
            className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"
            animate={{
              scale: [1.3, 1, 1.3],
              x: [50, 0, 50],
              y: [-50, 0, -50],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Chegou a hora de crescer<br />com gestão profissional
            </h2>
            <p className="text-xl text-white/90 mb-12">
              Veja como a BusiFarm pode transformar a forma como você gerencia seu negócio.
              Agende uma conversa sem compromisso.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/demonstracao">
                <motion.button
                  className="px-8 py-4 bg-white text-green-700 rounded-xl hover:shadow-2xl transition-all duration-300 flex items-center gap-2 justify-center group font-semibold text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Quero ver na prática
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </Link>

              <Link to="/solucoes">
                <motion.button
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white rounded-xl hover:bg-white/20 border border-white/20 transition-all duration-300 font-medium text-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Ver soluções completas
                </motion.button>
              </Link>
            </div>

            <p className="text-white/70 text-sm mt-8">
              Sem cartão de crédito. Sem letras miúdas. Apenas uma conversa honesta sobre como podemos ajudar.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
